import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { useShop } from "../context/ShopContext";
import { Input } from "@/components/ui/input";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatApiErrorDetail = (detail, fallback) => {
  if (detail == null) return fallback;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
};

const AuthPage = () => {
  const { t } = useShop();
  const { user, login, register } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState("login"); // login | register | forgot
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);

  if (user) {
    navigate(user.role === "admin" ? "/admin" : "/hesap", { replace: true });
    return null;
  }

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      if (mode === "forgot") {
        await axios.post(`${API}/auth/forgot-password`, { email: form.email });
        setForgotSent(true);
      } else {
        const data =
          mode === "login"
            ? await login(form.email, form.password)
            : await register(form.name, form.email, form.password);
        toast.success(mode === "login" ? t.auth.loginSuccess : t.auth.registerSuccess);
        navigate(data.role === "admin" ? "/admin" : "/hesap", { replace: true });
      }
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail, t.auth.errors.generic));
    } finally {
      setLoading(false);
    }
  };

  const switchMode = (m) => {
    setMode(m);
    setError("");
    setForgotSent(false);
  };

  return (
    <div className="min-h-screen bg-cream flex flex-col" data-testid="auth-page">
      <div className="grain-overlay" aria-hidden="true" />
      <header className="px-6 md:px-12 h-[72px] flex items-center justify-between border-b border-brown/10">
        <Link to="/" className="flex flex-col leading-none" data-testid="auth-logo">
          <span className="font-serif text-2xl font-medium tracking-tight text-brown">VLP Dolap</span>
          <span className="text-[10px] tracking-[0.35em] uppercase text-brown-muted mt-1">Volpar</span>
        </Link>
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-sm text-brown-light hover:text-brown transition-colors duration-300"
          data-testid="auth-back-home"
        >
          <ArrowLeft size={15} /> {t.auth.backHome}
        </Link>
      </header>

      <main className="flex-1 flex items-center justify-center px-6 py-16">
        <motion.div
          initial={{ opacity: 0, y: 32 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          className="w-full max-w-md"
        >
          {mode !== "forgot" && (
            <div className="flex border-b border-brown/10 mb-10" data-testid="auth-tabs">
              {["login", "register"].map((m) => (
                <button
                  key={m}
                  onClick={() => switchMode(m)}
                  className={`flex-1 pb-4 font-serif text-xl sm:text-2xl font-medium transition-colors duration-300 border-b-2 -mb-px ${
                    mode === m
                      ? "text-brown border-olive"
                      : "text-brown-muted border-transparent hover:text-brown-light"
                  }`}
                  data-testid={`auth-tab-${m}`}
                >
                  {m === "login" ? t.auth.login : t.auth.register}
                </button>
              ))}
            </div>
          )}

          <h1 className="font-serif text-3xl font-medium text-brown mb-4">
            {mode === "login" && t.auth.loginTitle}
            {mode === "register" && t.auth.registerTitle}
            {mode === "forgot" && t.auth.forgotTitle}
          </h1>
          {mode === "forgot" && (
            <p className="text-sm font-light text-brown-light mb-8">{t.auth.forgotSub}</p>
          )}
          {mode !== "forgot" && <div className="mb-6" />}

          {mode === "forgot" && forgotSent ? (
            <div data-testid="forgot-success">
              <p className="text-sm text-brown leading-relaxed border border-olive/30 bg-olive/5 p-6">
                {t.auth.forgotSentMsg}
              </p>
              <button
                onClick={() => switchMode("login")}
                className="mt-6 inline-flex items-center gap-2 text-sm font-medium text-olive-dark hover:text-brown transition-colors duration-300"
                data-testid="forgot-back-login"
              >
                <ArrowLeft size={14} /> {t.auth.backToLogin}
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="flex flex-col gap-8" data-testid="auth-form">
              {mode === "register" && (
                <div>
                  <label htmlFor="auth-name" className="text-[11px] tracking-[0.25em] uppercase text-brown-muted">
                    {t.auth.name}
                  </label>
                  <Input
                    id="auth-name"
                    required
                    minLength={2}
                    value={form.name}
                    onChange={set("name")}
                    className="editorial-input mt-1"
                    data-testid="auth-name-input"
                  />
                </div>
              )}
              <div>
                <label htmlFor="auth-email" className="text-[11px] tracking-[0.25em] uppercase text-brown-muted">
                  {t.auth.email}
                </label>
                <Input
                  id="auth-email"
                  type="email"
                  required
                  value={form.email}
                  onChange={set("email")}
                  className="editorial-input mt-1"
                  data-testid="auth-email-input"
                />
              </div>
              {mode !== "forgot" && (
                <div>
                  <label htmlFor="auth-password" className="text-[11px] tracking-[0.25em] uppercase text-brown-muted">
                    {t.auth.password}
                  </label>
                  <Input
                    id="auth-password"
                    type="password"
                    required
                    minLength={8}
                    value={form.password}
                    onChange={set("password")}
                    className="editorial-input mt-1"
                    data-testid="auth-password-input"
                  />
                  {mode === "register" ? (
                    <p className="mt-2 text-xs text-brown-muted font-light">{t.auth.passwordMin}</p>
                  ) : (
                    <button
                      type="button"
                      onClick={() => switchMode("forgot")}
                      className="mt-3 text-xs tracking-wide text-olive-dark hover:text-brown transition-colors duration-300"
                      data-testid="forgot-password-link"
                    >
                      {t.auth.forgotLink}
                    </button>
                  )}
                </div>
              )}

              {error && (
                <p className="text-sm text-red-800 bg-red-800/5 border border-red-800/15 px-4 py-3" data-testid="auth-error">
                  {error}
                </p>
              )}

              <button
                type="submit"
                disabled={loading}
                className="inline-flex items-center justify-center gap-3 px-8 py-4 rounded-full bg-brown text-cream text-sm font-medium tracking-wide hover:bg-olive-dark disabled:opacity-60 transition-colors duration-300"
                data-testid="auth-submit-button"
              >
                {loading && <Loader2 size={16} className="animate-spin" />}
                {loading
                  ? t.auth.submitting
                  : mode === "login"
                    ? t.auth.loginCta
                    : mode === "register"
                      ? t.auth.registerCta
                      : t.auth.forgotCta}
              </button>

              {mode === "forgot" && (
                <button
                  type="button"
                  onClick={() => switchMode("login")}
                  className="inline-flex items-center justify-center gap-2 text-sm text-brown-light hover:text-brown transition-colors duration-300"
                  data-testid="forgot-back-login-link"
                >
                  <ArrowLeft size={14} /> {t.auth.backToLogin}
                </button>
              )}
            </form>
          )}
        </motion.div>
      </main>
    </div>
  );
};

export default AuthPage;
